import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, ResourceLoaderParams, WritableSignal, effect, forwardRef, inject, input, model, resource, signal, viewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { lastValueFrom } from 'rxjs';
import { PhxCardComponent } from '../../../../../shared/components/phx-card/phx-card.component';
import { BaseListCombinedService } from '../../../../../@core/services/base-list-combined.service';
import { NotificationService } from '../../../../../@core/services/notification/notification.service';
import { Asset, AssetInput, Employee, EmployeeInput } from '../../../../../generated/generated-types';
import { AssetService } from '../../../../asset/services/asset.service';
import { WidgetAssetPreviewComponent } from '../../../../asset/widget-asset-preview/widget-asset-preview.component';
import { BaseModelInfoWidgetComponent } from '../../../../../shared/components/base-model-info-widget/base-model-info-widget.component';
import { EmployeeService } from '../../../../../shared/services/employee.service';

type EmployeeWithFeaturedAsset = Employee & {
  featuredAsset?: Asset | null;
  featuredAssetId?: string | null;
};

type EmployeeInputWithFeaturedAsset = EmployeeInput & {
  featuredAssetId?: string | null;
};

type AssetModelParams = {
  employee: EmployeeWithFeaturedAsset | undefined;
  fallbackAsset: Asset | null | undefined;
};

@Component({
  selector: 'app-employee-header-card-widget',
  templateUrl: './employee-header-card-widget.component.html',
  styleUrls: ['./employee-header-card-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => EmployeeHeaderCardWidgetComponent),
    multi: true
  }, EmployeeService],
  standalone: true,
  imports: [
    PhxCardComponent,
    WidgetAssetPreviewComponent,
    TooltipModule,
    TranslatePipe,
    BaseModelInfoWidgetComponent,
    CommonModule,
    ButtonModule,
  ]
})
export class EmployeeHeaderCardWidgetComponent implements ControlValueAccessor {

  phoneIsValid = true;
  mobilePhoneIsValid = true;
  emailIsValid = true;

  @Output() employeeChanged = new EventEmitter<Employee>();
  @Output() assetChoose = new EventEmitter<() => Promise<void>>();

  dataTestid = input<string>('employee-header-card-widget');

  displayName = model<string>('');

  employee = model<EmployeeWithFeaturedAsset>();
  @Input() formControl: FormControl;
  @Input() key: string;
  @Input() form: FormGroup;
  @Input() loadingSig: WritableSignal<boolean> = signal(false);
  @Input() fallbackAsset?: Asset | null;
  @Input() showContactActions = true;
  @Input() showIdentifier = true;

  assetModel = resource({
    params: () => ({ employee: this.employee(), fallbackAsset: this.fallbackAsset }),
    loader: ({ params }: ResourceLoaderParams<AssetModelParams>) => {
      const employee = params?.employee;
      if (employee?.featuredAsset) {
        return Promise.resolve({ asset: employee.featuredAsset });
      }
      if (employee?.featuredAssetId) {
        return lastValueFrom(this.assetService.getAsset(employee.featuredAssetId).single$)
          .then(res => ({ asset: res.asset }));
      }
      if (params?.fallbackAsset) {
        return Promise.resolve({ asset: params.fallbackAsset });
      }
      return Promise.resolve({ asset: null });
    }
  });
  assetPreview = viewChild(WidgetAssetPreviewComponent);

  onChange: (value: Asset) => void = (value) => {
    this.employee.update((prevEmployee) => ({
      ...prevEmployee,
      featuredAsset: value
    }));
  };
  onTouch: () => void = () => { };

  employeeService = inject(EmployeeService);

  constructor(
    private assetService: AssetService,
    private baseServices: BaseListCombinedService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
  ) {
    effect(() => {
      if (this.employee()?.name || this.employee()?.lastName) {
        this.initDisplayName();
      }
    });
  }

  ngOnInit() {
    this.syncEmployeeFromForm();
    this.formControl?.parent?.valueChanges.subscribe(() => {
      this.syncEmployeeFromForm();
    });
  }

  private getEmployeeFormSource(parent: FormGroup): FormGroup {
    const nestedEmployeeGroup = parent.get('employee');
    if (nestedEmployeeGroup instanceof FormGroup) {
      return nestedEmployeeGroup;
    }
    return parent;
  }

  private syncEmployeeFromForm(): void {
    const parent = this.formControl?.parent;
    if (!parent || !(parent instanceof FormGroup)) {
      return;
    }
    const employeeFormSource = this.getEmployeeFormSource(parent);
    this.phoneIsValid = employeeFormSource.get('phone')?.valid ?? true;
    this.mobilePhoneIsValid = employeeFormSource.get('mobilePhone')?.valid ?? true;
    this.emailIsValid = employeeFormSource.get('email')?.valid ?? true;
    const formValue = employeeFormSource.getRawValue() as EmployeeWithFeaturedAsset;
    const current = this.employee() ?? {};
    const employee: EmployeeWithFeaturedAsset = {
      ...current,
      ...formValue,
      id: formValue.id ?? current.id,
      featuredAsset: formValue.featuredAsset ?? current.featuredAsset,
      featuredAssetId: formValue.featuredAssetId ?? current.featuredAssetId,
    };
    this.employee.set(employee);
    this.onChangeEmployee(employee);
    this.cdr.markForCheck();
  }

  onChangeEmployee(employee: EmployeeWithFeaturedAsset) {
    if (employee?.name || employee?.lastName) {
      this.initDisplayName();
    }
  }

  initDisplayName() {
    const employee = this.employee();
    const parts = [employee?.name, employee?.lastName].filter(Boolean);
    this.displayName.set(parts.join(' '));
  }

  async onFilesSelected(files: File[]) {
    const savePromise = new Promise<void>((resolve) => {
      this.assetChoose.emit(async () => {
        resolve();
      });
    });

    if (this.assetChoose.observed) {
      await savePromise;
    }

    await this.saveAsset(files);
  }

  async saveAsset(files: File[]) {
    if (files?.length !== 1) {
      return;
    }

    const input: AssetInput = {
      file: files[0],
      name: 'employee-photo'
    };

    const savedAsset = await this.assetService.saveAsset(input);
    this.employee.update((prevEmployee) => ({
      ...prevEmployee,
      featuredAsset: savedAsset
    }));

    const updateEmployee: EmployeeInputWithFeaturedAsset = {
      id: this.employee().id,
      featuredAssetId: savedAsset.id
    };

    const savedEmployee = await this.employeeService.saveItem(updateEmployee) as EmployeeWithFeaturedAsset;

    if (this.formControl) {
      this.formControl.patchValue(savedAsset, { emitEvent: false });
    }
    if (!this.employee()?.id) {
      const employeeGroup = this.form.get('employee');
      if (employeeGroup) {
        employeeGroup.patchValue({ id: savedEmployee.id, featuredAssetId: savedAsset.id });
      } else {
        this.form.patchValue({ id: savedEmployee.id });
      }
    }

    this.employeeChanged.emit(savedEmployee);
    this.employee.set({ ...savedEmployee });
    const preview = this.assetPreview();
    if (preview) {
      preview.asset = savedEmployee.featuredAsset ?? savedAsset;
    }

    this.baseServices.notificationService.success(_('common.notify-upload-success'));
  }

  get photoEditable(): boolean {
    return !!this.employee()?.id;
  }

  get displayIdentifier(): string | undefined {
    const identifier = this.employee()?.identifier;
    return identifier != null && identifier !== '' ? String(identifier) : undefined;
  }

  showAssetLoader(): boolean {
    return this.loadingSig();
  }

  showInnerDataLoader(): boolean {
    return this.loadingSig();
  }

  callTo(phoneNumber: string) {
    window.open(`tel:${phoneNumber}`);
  }

  mailTo(email: string) {
    if (email.includes('@')) {
      window.open(`mailto:${email}`);
    }
  }

  writeValue(asset: Asset): void {
    this.employee.update((prevEmployee) => ({
      ...prevEmployee,
      featuredAsset: asset
    }));
  }

  registerOnChange(fn: (value: Asset) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }
}
